import { Component, ViewChild, OnInit, HostListener, inject } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ComponentCanDeactivate, Account, Solution } from '@aveva/connect-web-core';
import { combineLatest, Observable, of } from 'rxjs';
import * as moment from 'moment';
import { map, switchMap } from 'rxjs/operators';
import { Tenant, MaintenanceWindow } from './tenant';
import { MaintenanceService } from './maintenance.service';
import { ThemePalette } from '@angular/material/core';
import { AccountService } from 'src/app/shared/account.service';
import { SolutionsService } from 'src/app/shared/solutions.service';

@Component({
  selector: 'app-maintenance-edit',
  templateUrl: './maintenance-edit.component.html',
  styleUrls: ['./maintenance-edit.component.scss']
})
export class MaintenanceEditComponent implements OnInit, ComponentCanDeactivate {
  private maintenanceService = inject(MaintenanceService);
  private accountService = inject(AccountService);
  private solutionService = inject(SolutionsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  tenant?: Tenant;
  maintenanceWindow?: MaintenanceWindow;
  disabled = false;
  showSpinners = true;
  showSeconds = false;
  touchUi = false;
  enableMeridian = false;
  stepHour = 1;
  stepMinute = 1;
  stepSecond = 1;
  color: ThemePalette = 'primary';
  error: string;
  submitting = false;
  minDate: Date;
  tenant$: Observable<Tenant>;
  account$: Observable<Account>;
  solution$: Observable<Solution>;

  @ViewChild('form', { static: true }) form: NgForm;

  ngOnInit() {
    this.minDate = moment().toDate();
    const solutionid = this.route.snapshot.paramMap.get('solutionid');
    this.maintenanceService.getTenant(solutionid).pipe(
      switchMap(tenant => {
        this.account$ = this.accountService.getAccount(tenant.connectAccountId);
        this.solution$ = this.solutionService.getSolution(tenant.connectSolutionId);

        return combineLatest([of(tenant), this.account$, this.solution$]).pipe(
          map(([tenant, account, solution]) => {
            const activeMaintenanceWindow = this.getActiveMaintenanceWindow(tenant);
            const infrastructureTenantId = tenant.infrastructureTenantId || '';
            const aUnique = infrastructureTenantId.split('/')[0];
            return {
              ...tenant,
              connectAccountName: account.name,
              connectFolderName: (tenant.connectAccountId !== tenant.connectContextId) ? solution.name : '',
              connectFolderRegion: (tenant.connectAccountId !== tenant.connectContextId) ? solution.region : '',
              maintenanceStartTime: (activeMaintenanceWindow) ? activeMaintenanceWindow.startTimeUTC : '',
              maintenanceEndTime: (activeMaintenanceWindow) ? activeMaintenanceWindow.endTimeUTC : '',
              maintenanceNotes: (activeMaintenanceWindow) ? activeMaintenanceWindow.description : '',
              aUnique: aUnique
            };
          })
        );
      })
    ).subscribe(data => {
      this.tenant = data;
    });
  }

  private getActiveMaintenanceWindow(tenant: Tenant) {
    if (tenant !== null && tenant.maintenanceWindows != null) {
      return tenant.maintenanceWindows.find((maintenanceWindow: MaintenanceWindow) => {
        if (maintenanceWindow.isArchived === false) {
          const DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
          const currentDateTime = moment(new Date(), DATE_TIME_FORMAT);
          return (moment(currentDateTime) < moment(maintenanceWindow.endTimeUTC));
        }
        return false;
      });
    }
    return null;
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return !this.form.dirty || this.form.submitted;
  }

  isMaintenanceWindowValid() {
    const DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
    const currentDateTime = moment(new Date(), DATE_TIME_FORMAT);

    return ((this.tenant !== undefined) &&
      (moment(currentDateTime) < moment(this.tenant.maintenanceEndTime)) &&
      (moment(this.tenant.maintenanceStartTime) !== moment(this.tenant.maintenanceEndTime)) &&
      (moment(this.tenant.maintenanceStartTime) < moment(this.tenant.maintenanceEndTime)) &&
      this.tenant.maintenanceNotes !== '');
  }

  onSubmit() {
    if (this.submitting) {
      return;
    }
    this.minDate = moment(this.tenant.maintenanceStartTime).toDate();
    this.submitting = true;
    this.maintenanceWindow = {
      startTimeUTC: this.tenant.maintenanceStartTime,
      endTimeUTC: this.tenant.maintenanceEndTime,
      isArchived: false,
      description: this.tenant.maintenanceNotes
    };
    const obs = this.maintenanceService.updateMaintenance(this.tenant.connectSolutionId, this.maintenanceWindow);
    obs.subscribe(() => {
      this.submitting = false;
      this.router.navigate(['/maintenance'], { relativeTo: this.route });
    });
  }

}
